/**
 * Fills the [data-pdp-rating] slot on the PDP with a styled rating row
 * (score + gold stars + discrete "(N reseñas)" link), read from the real
 * data Judge.me already renders (data-average-rating / data-number-of-reviews
 * on its .jdgm-prev-badge). The reviews app itself is never touched — this
 * only standardizes how its existing data is displayed, mirroring the same
 * approach already used for product cards (assets/product-card-rating.js),
 * adapted here because the PDP has no `product-card` ancestor to scope from.
 *
 * Products with zero reviews still render a placeholder row (5 gray stars +
 * "(0)") instead of nothing, so the block never collapses and never causes a
 * layout jump once real reviews arrive — no score, no "Sin reseñas" text, no
 * link to click (there is nothing to scroll to yet).
 */

const STAR_PATH =
  'M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.27l-5.8 3.05 1.11-6.46-4.7-4.58 6.49-.94z';

/** @returns {string} */
function starsMarkup() {
  return `
    <span class="pdp-rating__star-empty">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
    </span>
    <span class="pdp-rating__star-fill">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
    </span>
  `;
}

/**
 * @param {number} rating
 * @returns {string}
 */
function buildStars(rating) {
  let html = '';
  for (let i = 1; i <= 5; i += 1) {
    const diff = rating - (i - 1);
    let fillPercent = 0;
    if (diff >= 1) fillPercent = 100;
    else if (diff >= 0.25) fillPercent = 50;
    html += `<span class="pdp-rating__star" style="--fill:${fillPercent}%">${starsMarkup()}</span>`;
  }
  return html;
}

/**
 * 5 unfilled, gray stars — same markup shape/size as buildStars() so the
 * block keeps the exact same height, just with no gold fill. Color is set
 * inline (not in CSS) since this file is the only one meant to change here.
 * @returns {string}
 */
function buildEmptyStars() {
  let html = '';
  for (let i = 1; i <= 5; i += 1) {
    html += `
      <span class="pdp-rating__star">
        <span class="pdp-rating__star-empty" style="color:#CFCFCF">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
        </span>
      </span>
    `;
  }
  return html;
}

/**
 * Scrolls to the full Judge.me reviews widget, wherever it renders on the
 * page, instead of depending on a hardcoded/fragile section id.
 */
function scrollToReviews(event) {
  event.preventDefault();
  const widget = document.querySelector('.jdgm-rev-widg, [id*="judge_me_reviews_review_widget"]');
  if (widget) widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * @param {Element} slot
 */
function fillSlot(slot) {
  const badge = document.querySelector('.jdgm-prev-badge');
  const count = badge ? parseInt(badge.getAttribute('data-number-of-reviews') || '0', 10) : 0;

  slot.setAttribute('role', 'img');
  slot.removeAttribute('aria-hidden');

  if (count > 0) {
    const rating = parseFloat(badge.getAttribute('data-average-rating') || '0');

    slot.innerHTML = `
      <span class="pdp-rating__score">${rating.toFixed(1)}</span>
      <span class="pdp-rating__stars">${buildStars(rating)}</span>
      <a href="#" class="pdp-rating__link" data-pdp-rating-link>(${count} reseña${count === 1 ? '' : 's'})</a>
    `;
    slot.setAttribute('aria-label', `${rating.toFixed(1)} de 5 estrellas, ${count} reseña${count === 1 ? '' : 's'}`);

    const link = slot.querySelector('[data-pdp-rating-link]');
    if (link) link.addEventListener('click', scrollToReviews);
    return;
  }

  slot.innerHTML = `
    <span class="pdp-rating__stars">${buildEmptyStars()}</span>
    <span class="pdp-rating__count" style="color:#9CA3AF">(0)</span>
  `;
  slot.setAttribute('aria-label', 'Sin reseñas todavía');
}

function run() {
  document.querySelectorAll('[data-pdp-rating]').forEach(fillSlot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run);
} else {
  run();
}
